import { RefObject, useCallback, useEffect, useRef } from 'react';

type useStickyAutoScrollParams = {
  containerRef: RefObject<HTMLDivElement | null>;
  contentRef: RefObject<HTMLDivElement | null>;
  resetKey: string;
  threshold?: number;
};

export function useStickyAutoScroll({
  containerRef,
  contentRef,
  resetKey,
  threshold = 50,
}: useStickyAutoScrollParams) {
  const stickToBottomRef = useRef<boolean>(true);
  const prevResetKeyRef = useRef<string | null>(null);

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      const element = containerRef.current;
      if (!element) return;
      element.scrollTo({ top: element.scrollHeight, behavior });
    },
    [containerRef],
  );

  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;

    if (prevResetKeyRef.current !== resetKey) {
      prevResetKeyRef.current = resetKey;
      stickToBottomRef.current = true;
      requestAnimationFrame(() => scrollToBottom('auto'));
    }

    if (!container || !content) return;

    const observer = new ResizeObserver(() => {
      if (!stickToBottomRef.current) return;
      requestAnimationFrame(() => scrollToBottom('auto'));
    });
    observer.observe(content);
    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, [containerRef, contentRef, resetKey, scrollToBottom]);

  const onScroll = useCallback(() => {
    const element = containerRef.current;
    if (!element) return;
    const distanceFromBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    stickToBottomRef.current = distanceFromBottom <= threshold;
  }, [containerRef, threshold]);

  const resetSticky = useCallback(() => {
    stickToBottomRef.current = true;
  }, []);

  const pauseStickyForFrame = useCallback(() => {
    // Pause sticky behavior until the user scrolls again.
    stickToBottomRef.current = false;
  }, []);

  return { onScroll, scrollToBottom, resetSticky, pauseStickyForFrame };
}
